import { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogActions, TextField, Button, Divider, Typography,
} from '@mui/material';
import { CompanyInGroup } from '@/api/endpoints/group';
import BrandedDialogTitle from '@/components/common/BrandedDialogTitle';
import AddressForm from '@/components/common/AddressForm';
import { DEFAULT_PALETTE } from '@/config/colorPalettes';

// ────────────────────────────────────────────
// Company Add / Edit Dialog (shared)
//
// One dialog drives two flows:
//  - Create (company == null): used to add a company to the group. Country is
//    required because it picks the tax jurisdiction + chart of accounts.
//  - Edit (company set): patch an existing company's branding/leadership.
//
// Lives here (not inline in a page) so both Settings → Group Settings and the
// Group → Companies page open the SAME dialog instead of duplicating it.
// ────────────────────────────────────────────
export interface CompanyFormDialogProps {
  open: boolean;
  company: CompanyInGroup | null;
  onClose: () => void;
  onSave: (id: string, data: Partial<CompanyInGroup>) => void;
  onCreate?: (data: Record<string, string>) => void;
  saving: boolean;
}

export default function CompanyFormDialog({
  open, company, onClose, onSave, onCreate, saving,
}: CompanyFormDialogProps) {
  const isCreate = !company;
  const [form, setForm] = useState({
    name: '',
    short_code: '',
    colour: DEFAULT_PALETTE.primary,
    address_line_1: '',
    address_line_2: '',
    city: '',
    lga: '',
    state: '',
    postal_code: '',
    country: '',
    email: '',
    ceo_name: '',
    ceo_title: '',
    ceo_email: '',
    ceo_phone: '',
  });

  useEffect(() => {
    if (company) {
      setForm({
        name: company.name || '',
        short_code: company.short_code || '',
        colour: company.colour || DEFAULT_PALETTE.primary,
        address_line_1: (company as any).address_line_1 || '',
        address_line_2: (company as any).address_line_2 || '',
        city: (company as any).city || '',
        lga: (company as any).lga || '',
        state: (company as any).state || '',
        postal_code: (company as any).postal_code || '',
        country: (company as any).country_code || '',
        email: '',
        ceo_name: company.ceo_name || '',
        ceo_title: company.ceo_title || '',
        ceo_email: company.ceo_email || '',
        ceo_phone: company.ceo_phone || '',
      });
    } else {
      setForm({
        name: '', short_code: '', colour: DEFAULT_PALETTE.primary,
        address_line_1: '', address_line_2: '', city: '', lga: '', state: '', postal_code: '',
        country: '', email: '', ceo_name: '', ceo_title: '', ceo_email: '', ceo_phone: '',
      });
    }
  }, [company, open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    if (company) {
      onSave(company.id, form);
    } else if (onCreate) {
      onCreate({ ...form, country_code: form.country });
    }
  };

  const canSubmit = isCreate
    ? !saving && !!form.name && !!form.country
    : !saving && !!form.name && !!form.short_code;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <BrandedDialogTitle>{isCreate ? 'Add Company' : 'Edit Company'}</BrandedDialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        <TextField label="Company Name" name="name" value={form.name} onChange={handleChange} required fullWidth />
        {!isCreate && (
          <TextField label="Short Code" name="short_code" value={form.short_code} onChange={handleChange} required fullWidth inputProps={{ maxLength: 10 }} />
        )}
        <TextField label="Email" name="email" type="email" value={form.email} onChange={handleChange} fullWidth />
        <AddressForm
          values={{ address_line_1: form.address_line_1, address_line_2: form.address_line_2, city: form.city, lga: form.lga, state: form.state, postal_code: form.postal_code, country: form.country }}
          onChange={(field, value) => setForm(prev => ({ ...prev, [field]: value }))}
          defaultCountryCode={form.country}
          compact
        />
        {isCreate && !form.country && (
          <Typography variant="caption" color="error">Country is required — it determines the tax jurisdiction and chart of accounts.</Typography>
        )}
        <Divider><Typography variant="caption" color="text.secondary">Leadership</Typography></Divider>
        <TextField label="CEO / MD Name" name="ceo_name" value={form.ceo_name} onChange={handleChange} fullWidth placeholder="e.g. Jane Smith" />
        <TextField label="Title" name="ceo_title" value={form.ceo_title} onChange={handleChange} fullWidth placeholder="e.g. Managing Director, CEO" />
        <TextField label="CEO Email" name="ceo_email" type="email" value={form.ceo_email} onChange={handleChange} fullWidth />
        <TextField label="CEO Phone" name="ceo_phone" value={form.ceo_phone} onChange={handleChange} fullWidth />
        <Divider />
        <TextField label="Brand Colour" name="colour" type="color" value={form.colour} onChange={handleChange} fullWidth slotProps={{ input: { style: { height: 56 } } }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={handleSubmit}>
          {saving ? (isCreate ? 'Creating...' : 'Saving...') : (isCreate ? 'Create Company' : 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
